import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTelegramStartParam } from './useUserData';
import useInterfaceStore from '../stores/useInterfaceStore';

/** Survives MapCanvas StrictMode remount / early clear until City Hall vote UI mounts. */
export const DEEPLINK_CITYHALL_VOTE_KEY = 'deeplink_open_cityhall_vote';

/** True when a `cityhall` deep link is still waiting to open the vote screen. */
export function hasPendingCityHallVoteDeepLink(): boolean {
  try {
    return sessionStorage.getItem(DEEPLINK_CITYHALL_VOTE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Forget a pending `cityhall` deep link (used, or dropped during onboarding). */
export function clearPendingCityHallVoteDeepLink(): void {
  try {
    sessionStorage.removeItem(DEEPLINK_CITYHALL_VOTE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Parses Telegram startapp deep-link params once on mount and stores a pending
 * camera-focus target in useInterfaceStore so MapCanvas can act on it after the
 * map finishes loading.
 *
 * Supported formats (namespaced to avoid collisions with existing params):
 *   plot_<mapObjectId>  — smooth camera move to a specific map object
 *   cityhall            — open Town Hall on the voting screen (election CTAs)
 *   cityhall_vote       — same as cityhall (explicit alias)
 *   elections           — same as cityhall
 *   tap / energy        — open the tap screen (the bot's "Top up energy" button)
 */
export function useDeepLinkStartParam(): void {
  const setPendingDeepLinkFocus = useInterfaceStore((s) => s.setPendingDeepLinkFocus);
  const navigate = useNavigate();
  const parsedRef = useRef(false);

  useEffect(() => {
    if (parsedRef.current) return;

    const applyParam = (param: string) => {
      parsedRef.current = true;

      if (param === 'cityhall' || param === 'cityhall_vote' || param === 'elections') {
        // Election Telegram buttons use startapp=cityhall — open vote_list, not only a camera pan.
        try {
          sessionStorage.setItem(DEEPLINK_CITYHALL_VOTE_KEY, '1');
        } catch {
          /* ignore */
        }
        setPendingDeepLinkFocus({ type: 'cityhall', openVote: true });
        return;
      }

      if (param === 'tap' || param === 'energy') {
        // Unfinished onboarding still wins: its gate redirects to the stories.
        navigate('/tap', { replace: true });
        return;
      }

      const plotMatch = param.match(/^plot_(\d+)$/);
      if (plotMatch) {
        const mapObjectId = parseInt(plotMatch[1], 10);
        if (!isNaN(mapObjectId)) {
          setPendingDeepLinkFocus({ type: 'plot', mapObjectId });
        }
      }
    };

    const tryParse = (): boolean => {
      const param = getTelegramStartParam();
      if (!param) return false;
      applyParam(param);
      return true;
    };

    if (tryParse()) return;

    // Hash/start_param can land a tick after first paint (Telegram WebView).
    const t0 = window.setTimeout(() => {
      if (!parsedRef.current) tryParse();
    }, 0);
    const t1 = window.setTimeout(() => {
      if (!parsedRef.current) tryParse();
      parsedRef.current = true; // stop retrying either way
    }, 750);

    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [setPendingDeepLinkFocus, navigate]);
}
